/*
 * McDonald's x Yellowpop landing page.
 * Each section root has a data-mcd attribute. The value selects the module below.
 * The modules use the Shopify AJAX cart API, so the receipt, the store header cart and the cart drawer are always the same.
 */
(function () {
  'use strict';

  if (window.McdCollab) return;

  var routesRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Helpers ---------- */

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function readJSON(root, selector) {
    var el = root.querySelector(selector);
    if (!el) return {};
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return {};
    }
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // Replaces [key] placeholders in a setting text.
  function fill(text, values) {
    return String(text || '').replace(/\[(\w+)\]/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : match;
    });
  }

  // Formats cents like the Liquid filter money_without_trailing_zeros.
  function formatMoney(cents, format) {
    format = String(format || '${{amount}}').replace(/<[^>]*>/g, '');
    cents = parseInt(cents, 10) || 0;

    function delimit(precision, thousands, decimal) {
      var parts = (cents / 100).toFixed(precision).split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return parts[1] ? whole + decimal + parts[1] : whole;
    }

    var match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    var value;
    switch (match && match[1]) {
      case 'amount_no_decimals': value = delimit(0, ',', '.'); break;
      case 'amount_with_comma_separator': value = delimit(2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = delimit(0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = delimit(2, "'", '.'); break;
      default: value = delimit(2, ',', '.');
    }
    value = value.replace(/[.,]00$/, '');
    return format.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  function storageGet(store, key) {
    try {
      return window[store].getItem(key);
    } catch (e) {
      return null;
    }
  }

  function storageSet(store, key, value) {
    try {
      window[store].setItem(key, value);
    } catch (e) {
      /* The browser blocks the storage. The page works without it. */
    }
  }


  function errorMessage(error, fallback) {
    return (error && (error.description || error.message)) || fallback;
  }

  /* ---------- Cart ---------- */

  function cartAdd(items) {
    return fetch(routesRoot + 'cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok) throw data;
          return data;
        });
      })
      .then(function (data) {
        document.dispatchEvent(new CustomEvent('mcd:cart:changed', { detail: data }));
        // The theme drawer (bundle.js) updates the header cart count and the drawer content on this event.
        window.dispatchEvent(new CustomEvent('drawer:cart:update'));
        return data;
      });
  }

  function cartGet() {
    return fetch(routesRoot + 'cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(function (res) {
      return res.json();
    });
  }

  /* ---------- Modules ---------- */

  var modules = {};

  // The design sizes the hero and the kiosk with the header height (--yph-h). The page uses the store header.
  function syncHeaderHeight() {
    var header = document.getElementById('global-header');
    var h = header ? Math.round(header.getBoundingClientRect().height) : 0;
    document.documentElement.style.setProperty('--yph-h', h + 'px');
  }
  syncHeaderHeight();
  window.addEventListener('resize', syncHeaderHeight);
  if ('ResizeObserver' in window) {
    var headerEl = document.getElementById('global-header');
    if (headerEl) new ResizeObserver(syncHeaderHeight).observe(headerEl);
  }

  function openCartDrawer() {
    window.dispatchEvent(new CustomEvent('drawer:cart:update'));
    window.dispatchEvent(new CustomEvent('drawer:cart'));
  }

  // Fills the empty space next to a contained image with the colors of the image edges (design behavior).
  function edgeBackground(img) {
    try {
      var c = document.createElement('canvas');
      var w = 40;
      var h = 40;
      c.width = w;
      c.height = h;
      var ctx = c.getContext('2d');
      ctx.drawImage(img, 0, 0, w, h);
      var d = ctx.getImageData(0, 0, w, h).data;
      var column = function (x0, x1) {
        var stops = [];
        for (var k = 0; k < 8; k++) {
          var r = 0, g = 0, b = 0, n = 0;
          for (var y = k * 5; y < k * 5 + 5; y++) {
            for (var x = x0; x < x1; x++) {
              var i = (y * w + x) * 4;
              r += d[i]; g += d[i + 1]; b += d[i + 2]; n++;
            }
          }
          stops.push('rgb(' + (r / n | 0) + ',' + (g / n | 0) + ',' + (b / n | 0) + ')');
        }
        return 'linear-gradient(to bottom,' + stops.join(',') + ')';
      };
      img.parentNode.style.background = column(0, 1) + ' left/50.5% 100% no-repeat,' + column(w - 1, w) + ' right/50.5% 100% no-repeat';
    } catch (e) {
      /* The image is from another origin without CORS. The slide keeps its plain background. */
    }
  }

  // Kiosk: category tabs, product popup with gallery, add to cart and receipt.
  modules.kiosk = function (root) {
    var cfg = readJSON(root, '[data-kx-config]');
    var tabs = toArray(root.querySelectorAll('.k-tab'));
    var allCards = toArray(root.querySelectorAll('.k-card'));
    var cards = toArray(root.querySelectorAll('.k-card[data-product-id]'));
    var countEl = root.querySelector('[data-kx-count]');
    var totalEl = root.querySelector('[data-kx-total]');
    var receiptEl = root.querySelector('[data-kx-receipt]');
    var checkoutBtn = root.querySelector('[data-kx-checkout]');
    var freeShipping = parseInt(root.getAttribute('data-free-shipping'), 10) || 0;
    var shippingFee = parseInt(root.getAttribute('data-shipping-fee'), 10) || 0;

    var modal = root.querySelector('[data-kx-modal]');
    var modalBox = modal.querySelector('.k-modal');
    var modalImg = root.querySelector('[data-kx-modal-img]');
    var modalTitle = root.querySelector('[data-kx-modal-title]');
    var modalPrice = root.querySelector('[data-kx-modal-price]');
    var modalDesc = root.querySelector('[data-kx-modal-desc]');
    var moreBtn = root.querySelector('[data-kx-more]');
    var learnLink = root.querySelector('[data-kx-learn]');
    var materialRow = root.querySelector('[data-kx-modal-material-row]');
    var materialEl = root.querySelector('[data-kx-modal-material]');
    var shipRow = root.querySelector('[data-kx-modal-ship-row]');
    var shipEl = root.querySelector('[data-kx-modal-ship]');
    var variantRow = root.querySelector('[data-kx-variant-row]');
    var variantSelect = root.querySelector('[data-kx-variant]');
    var qtyEl = root.querySelector('[data-kx-qty]');
    var qtyVal = qtyEl.querySelector('.k-qty-val');
    var addBtn = root.querySelector('[data-kx-add]');
    var errorEl = root.querySelector('[data-kx-error]');
    var closeBtn = root.querySelector('[data-kx-close]');

    var active = null;
    var lastFocus = null;
    var qty = 1;
    var prevLines = null;
    var prevCount = null;

    function money(cents) {
      return formatMoney(cents, cfg.moneyFormat);
    }

    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        var cat = tab.getAttribute('data-cat');
        tabs.forEach(function (t) {
          var on = t === tab;
          t.classList.toggle('active', on);
          t.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        allCards.forEach(function (card) {
          var show = cat === 'all' || card.getAttribute('data-cat') === cat;
          card.style.display = show ? '' : 'none';
        });
      });
    });

    function render(cart) {
      var count = cart.item_count || 0;
      var total = cart.total_price || 0;

      countEl.textContent = count;
      totalEl.textContent = money(total);
      if (prevCount !== null && prevCount !== count) {
        countEl.classList.remove('bump');
        void countEl.offsetWidth;
        countEl.classList.add('bump');
      }
      prevCount = count;

      var inCart = {};
      (cart.items || []).forEach(function (item) {
        inCart[item.product_id] = true;
      });
      cards.forEach(function (card) {
        card.classList.toggle('selected', !!inCart[card.getAttribute('data-product-id')]);
      });

      var html =
        '<div class="kx-rc-head">' + escapeHtml(cfg.receiptTitle) + '</div>' +
        '<div class="kx-rc-meta">' + escapeHtml((cfg.receiptOrder || '') + ' ' + (cfg.orderNumber || '')) + '</div>' +
        '<div class="kx-rc-rule"></div>';

      if (count > 0) {
        var nextLines = {};
        html += cart.items.map(function (item) {
          var isNew = prevLines !== null && prevLines[item.key] !== item.quantity;
          nextLines[item.key] = item.quantity;
          return '<div class="kx-rc-line' + (isNew ? ' new' : '') + '"><span>' + item.quantity + '× ' +
            escapeHtml(item.product_title + (item.product_has_only_default_variant || !item.variant_title ? "" : " - " + item.variant_title)) + '</span><span>' + escapeHtml(money(item.final_line_price)) + '</span></div>';
        }).join('');
        prevLines = nextLines;
        html += '<div class="kx-rc-rule"></div><div class="kx-rc-total"><span>' + escapeHtml(cfg.receiptTotal) +
          '</span><span>' + escapeHtml(money(total)) + '</span></div>';
        if (freeShipping > 0) {
          var note = total >= freeShipping
            ? cfg.shippingUnlocked
            : fill(cfg.shippingRemaining, { amount: money(freeShipping - total), fee: money(shippingFee) });
          html += '<div class="kx-rc-meta" style="margin:8px 0 0">' + escapeHtml(note) + '</div>';
        }
      } else {
        prevLines = {};
        html += '<div class="kx-rc-empty">' + escapeHtml(cfg.receiptEmpty) + '</div>';
      }
      receiptEl.innerHTML = html;
    }

    function refresh() {
      cartGet().then(render).catch(function () {
        /* The receipt keeps the last state. */
      });
    }

    function setQty(value) {
      qty = Math.max(1, value);
      qtyEl.setAttribute('data-qty', qty);
      qtyVal.textContent = qty;
    }

    function currentVariant() {
      if (!active) return null;
      var variants = active.variants || [];
      var id = variantRow.hidden ? active.selectedId : parseInt(variantSelect.value, 10);
      for (var i = 0; i < variants.length; i++) {
        if (variants[i].id === id) return variants[i];
      }
      return variants[0] || null;
    }

    function updateVariant() {
      var variant = currentVariant();
      if (!variant) return;
      modalPrice.textContent = money(variant.price);
      addBtn.disabled = !variant.available;
      addBtn.textContent = variant.available ? addBtn.getAttribute('data-label') : addBtn.getAttribute('data-soldout');
    }

    // Gallery: one slide for each product image, with arrows and dots (design markup).
    function buildGallery(images, title) {
      if (!images.length) {
        modalImg.innerHTML = '';
        return;
      }
      var slides = images.map(function (src, i) {
        return '<div class="kg-slide"><img class="kg-main" crossorigin="anonymous" src="' + escapeHtml(src) + '" alt="' + (i === 0 ? escapeHtml(title) : '') + '"></div>';
      }).join('');
      var dots = images.map(function (src, i) {
        return '<button type="button" class="kg-dot' + (i === 0 ? ' on' : '') + '" aria-label="' + escapeHtml(fill(cfg.galleryDot, { n: i + 1 })) + '"></button>';
      }).join('');
      var controls = images.length > 1
        ? '<button type="button" class="kg-arrow kg-prev" aria-label="' + escapeHtml(cfg.galleryPrev) + '">‹</button><button type="button" class="kg-arrow kg-next" aria-label="' + escapeHtml(cfg.galleryNext) + '">›</button><div class="kg-dots">' + dots + '</div>'
        : '';
      modalImg.innerHTML = '<div class="kg-track">' + slides + '</div>' + controls;

      var track = modalImg.querySelector('.kg-track');
      var dotEls = toArray(modalImg.querySelectorAll('.kg-dot'));
      var go = function (i) {
        track.scrollTo({ left: i * track.clientWidth, behavior: reduceMotion ? 'auto' : 'smooth' });
      };
      var cur = function () {
        return Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
      };
      track.addEventListener('scroll', function () {
        var c = cur();
        dotEls.forEach(function (d, i) {
          d.classList.toggle('on', i === c);
        });
      });
      dotEls.forEach(function (d, i) {
        d.addEventListener('click', function (e) {
          e.stopPropagation();
          go(i);
        });
      });
      var prev = modalImg.querySelector('.kg-prev');
      var next = modalImg.querySelector('.kg-next');
      if (prev) prev.addEventListener('click', function () { go(Math.max(0, cur() - 1)); });
      if (next) next.addEventListener('click', function () { go(Math.min(images.length - 1, cur() + 1)); });
      toArray(modalImg.querySelectorAll('img.kg-main')).forEach(function (img) {
        if (img.complete) edgeBackground(img);
        else img.addEventListener('load', function () { edgeBackground(img); });
      });
    }

    function openModal(data) {
      active = data;
      lastFocus = document.activeElement;

      buildGallery(active.images || [], active.title || '');
      modalBox.classList.remove('more');
      moreBtn.textContent = cfg.moreLabel;
      moreBtn.setAttribute('aria-expanded', 'false');
      modalTitle.textContent = active.title || '';
      modalDesc.textContent = active.description || '';
      materialEl.textContent = active.material || '';
      materialRow.hidden = !active.material;
      shipEl.textContent = active.shipping || '';
      shipRow.hidden = !active.shipping;
      if (learnLink) {
        // Link to the product page. The product grid cards also open this popup.
        var learnLabel = active.learnLabel || cfg.learnLabel || '';
        learnLink.textContent = learnLabel;
        learnLink.href = active.url || '#';
        learnLink.hidden = !(learnLabel && active.url);
      }

      var variants = active.variants || [];
      if (variants.length > 1) {
        variantSelect.innerHTML = variants.map(function (v) {
          return '<option value="' + v.id + '"' + (v.id === active.selectedId ? ' selected' : '') + '>' + escapeHtml(v.title) + '</option>';
        }).join('');
        variantRow.hidden = false;
      } else {
        variantSelect.innerHTML = '';
        variantRow.hidden = true;
      }

      errorEl.textContent = '';
      setQty(1);
      updateVariant();
      modal.classList.add('open');
      document.body.classList.add('mcd-lock');
      modalBox.focus({ preventScroll: true });
    }

    function openCard(card) {
      var dataEl = card.querySelector('[data-k-product]');
      if (!dataEl) return;
      try {
        openModal(JSON.parse(dataEl.textContent));
      } catch (e) {
        /* Invalid product data: no popup. */
      }
    }

    function closeModal() {
      if (!modal.classList.contains('open')) return;
      modal.classList.remove('open');
      document.body.classList.remove('mcd-lock');
      active = null;
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    allCards.forEach(function (card) {
      if (!card.hasAttribute('data-product-id')) return;
      card.addEventListener('click', function () {
        openCard(card);
      });
      card.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openCard(card);
        }
      });
    });

    moreBtn.addEventListener('click', function () {
      var open = modalBox.classList.toggle('more');
      moreBtn.textContent = open ? cfg.lessLabel : cfg.moreLabel;
      moreBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
    variantSelect.addEventListener('change', updateVariant);
    root.querySelector('[data-kx-qty-minus]').addEventListener('click', function () {
      setQty(qty - 1);
    });
    root.querySelector('[data-kx-qty-plus]').addEventListener('click', function () {
      setQty(qty + 1);
    });

    addBtn.addEventListener('click', function () {
      var variant = currentVariant();
      if (!variant || !variant.available) return;
      addBtn.disabled = true;
      errorEl.textContent = '';
      cartAdd([{ id: variant.id, quantity: qty }]).then(
        function () {
          addBtn.disabled = false;
          closeModal();
        },
        function (error) {
          errorEl.textContent = errorMessage(error, cfg.errorText);
          updateVariant();
        }
      );
    });

    closeBtn.addEventListener('click', closeModal);
    modal.addEventListener('click', function (e) {
      if (e.target === modal) closeModal();
    });

    function onKeydown(e) {
      if (e.key === 'Escape') closeModal();
    }
    document.addEventListener('keydown', onKeydown);
    document.addEventListener('mcd:cart:changed', refresh);
    // The cart drawer of the theme can change the cart too.
    window.addEventListener('drawer:cart:rendered', refresh);
    window.addEventListener('pageshow', refresh);

    checkoutBtn.addEventListener('click', function () {
      var action = root.getAttribute('data-checkout-action');
      if (action === 'drawer' && window.theme) {
        openCartDrawer();
        return;
      }
      window.location.href = routesRoot + (action === 'checkout' ? 'checkout' : 'cart');
    });

    // The product grid opens this popup.
    McdCollab.openProduct = openModal;

    refresh();

    return {
      destroy: function () {
        document.removeEventListener('keydown', onKeydown);
        document.removeEventListener('mcd:cart:changed', refresh);
        window.removeEventListener('drawer:cart:rendered', refresh);
        window.removeEventListener('pageshow', refresh);
        document.body.classList.remove('mcd-lock');
        if (McdCollab.openProduct === openModal) McdCollab.openProduct = null;
      }
    };
  };

  // Mystery box: bundle options, reveal animation and the box image of the selected bundle.
  modules.mystery = function (root) {
    var cfg = readJSON(root, '[data-mbx-config]');
    var opts = toArray(root.querySelectorAll('.mbx-opt[data-variant-id]'));
    var cards = toArray(root.querySelectorAll('.mbx-card'));
    var visuals = toArray(root.querySelectorAll('.mbx-visual img[data-id]'));
    var badge = root.querySelector('[data-mbx-badge]');
    var cta = root.querySelector('[data-mbx-cta]');
    var toast = root.querySelector('[data-mbx-toast]');
    var bar = root.querySelector('[data-mbx-bar]');
    var barName = root.querySelector('[data-mbx-bar-name]');
    var barPrice = root.querySelector('[data-mbx-bar-price]');
    var barCta = root.querySelector('[data-mbx-bar-cta]');

    var selected = null;
    opts.forEach(function (opt) {
      if (opt.classList.contains('on')) selected = opt;
    });
    selected = selected || opts[0] || null;
    var busy = false;
    var timers = [];
    var toastTimer = null;
    var observer = null;

    function money(cents) {
      return formatMoney(cents, cfg.moneyFormat);
    }

    function render() {
      opts.forEach(function (opt) {
        var on = opt === selected;
        opt.classList.toggle('on', on);
        opt.setAttribute('aria-checked', on ? 'true' : 'false');
      });
      if (!selected) return;

      var id = selected.getAttribute('data-id');
      visuals.forEach(function (img) {
        img.classList.toggle('on', img.getAttribute('data-id') === id);
      });
      badge.textContent = selected.getAttribute('data-badge') || '';

      var name = selected.getAttribute('data-name');
      var price = parseInt(selected.getAttribute('data-price'), 10) || 0;
      var available = selected.getAttribute('data-available') === 'true';
      if (cta) {
        cta.textContent = busy ? cfg.busyLabel : fill(cfg.ctaLabel, { name: name, price: money(price) });
        cta.disabled = busy || !available;
      }
      if (bar) {
        barName.textContent = name;
        barPrice.textContent = money(price);
        barCta.textContent = busy ? cfg.busyLabel : cfg.barLabel;
        barCta.disabled = busy || !available;
      }
    }

    function showToast(message) {
      clearTimeout(toastTimer);
      toast.textContent = message;
      toast.classList.toggle('above-bar', !!bar && bar.classList.contains('show'));
      toast.classList.add('show');
      toastTimer = setTimeout(function () {
        toast.classList.remove('show');
      }, 2800);
    }

    function clearLit() {
      cards.forEach(function (card) {
        card.classList.remove('lit');
      });
    }

    function later(fn, ms) {
      timers.push(setTimeout(fn, ms));
    }

    function finish() {
      var option = selected;
      cartAdd([{ id: parseInt(option.getAttribute('data-variant-id'), 10), quantity: 1 }]).then(
        function () {
          clearLit();
          busy = false;
          render();
          showToast(option.getAttribute('data-toast') || fill(cfg.addedToast, { name: option.getAttribute('data-name') }));
        },
        function (error) {
          clearLit();
          busy = false;
          render();
          showToast(errorMessage(error, cfg.errorText));
        }
      );
    }

    function addMystery() {
      if (busy || !selected || selected.getAttribute('data-available') !== 'true') return;
      busy = true;
      render();

      var total = cards.length;
      if (!total || reduceMotion) {
        finish();
        return;
      }

      // Every box: the light runs through the cards two times, slows down and always stops on the
      // same card (the Mystery Box card), so the visitor does not see which sign they get.
      var landOn = parseInt(cfg.landOn, 10) || total;
      var target = Math.min(Math.max(landOn, 1), total) - 1;
      var steps = 2 * total + target + 1;
      var step = 0;
      (function tick() {
        clearLit();
        cards[step % total].classList.add('lit');
        step++;
        if (step >= steps) {
          later(finish, 900);
          return;
        }
        var progress = step / steps;
        later(tick, 60 + progress * progress * 420);
      })();
    }

    opts.forEach(function (opt) {
      opt.addEventListener('click', function () {
        if (busy) return;
        selected = opt;
        render();
      });
    });

    if (cta) cta.addEventListener('click', addMystery);
    if (barCta) barCta.addEventListener('click', addMystery);

    // Signs with a product open the kiosk product popup. Without the kiosk, they open the product page.
    cards.forEach(function (card) {
      var dataEl = card.querySelector('[data-k-product]');
      if (!dataEl) return;
      function open() {
        if (busy) return;
        var data;
        try {
          data = JSON.parse(dataEl.textContent);
        } catch (e) {
          return;
        }
        if (McdCollab.openProduct) McdCollab.openProduct(data);
        else if (data.url) window.location.href = data.url;
      }
      card.addEventListener('click', open);
      card.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          open();
        }
      });
    });

    if (bar && 'IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            bar.classList.toggle('show', entry.isIntersecting);
          });
        },
        { threshold: 0.15 }
      );
      observer.observe(root);
    }

    render();

    return {
      destroy: function () {
        timers.forEach(clearTimeout);
        clearTimeout(toastTimer);
        if (observer) observer.disconnect();
      }
    };
  };

  // Product grid: "Shop now" opens the kiosk popup. Without a kiosk, it opens the product page.
  modules.grid = function (root) {
    toArray(root.querySelectorAll('.pg-card[data-product-id]')).forEach(function (card) {
      var btn = card.querySelector('[data-pg-open]');
      var dataEl = card.querySelector('[data-k-product]');
      if (!btn || !dataEl) return;
      // Popup action only. Without the kiosk popup, the link opens the product page.
      btn.addEventListener('click', function (e) {
        var data;
        try {
          data = JSON.parse(dataEl.textContent);
        } catch (err) {
          return;
        }
        if (!McdCollab.openProduct) return;
        e.preventDefault();
        McdCollab.openProduct(data);
      });
    });
    return null;
  };

  // Video: replaces the poster with the player. Without a video, the button does nothing.
  modules.video = function (root) {
    var player = root.querySelector('[data-video-player]');
    var template = root.querySelector('[data-video-template]');
    if (!player || !template) return null;

    function play() {
      if (player.classList.contains('playing')) return;
      player.classList.add('playing');
      player.innerHTML = '';
      player.appendChild(template.content.cloneNode(true));
      var video = player.querySelector('video');
      if (video && video.play) {
        var attempt = video.play();
        if (attempt && attempt.catch) attempt.catch(function () {});
      }
    }

    player.addEventListener('click', play);
    return null;
  };

  // Golden slots: email gate, one spin, prizes, copy code and the welcome popup.
  modules.slots = function (root) {
    var cfg = readJSON(root, '[data-slots-config]');
    var symbols = (cfg.symbols || []).filter(function (s) {
      return s.src;
    });
    // The API chooses the result. The prize blocks only give the symbol and the texts of each result.
    var prizes = cfg.prizes || [];

    var form = root.querySelector('[data-slots-form]');
    var input = form.querySelector('input[type="email"]');
    var errorEl = root.querySelector('[data-slots-error]');
    var machine = root.querySelector('[data-slots-machine]');
    var pullBtn = root.querySelector('[data-slots-pull]');
    var knob = root.querySelector('[data-slots-knob]');
    var resultBox = root.querySelector('[data-slots-result]');
    var resultTitle = root.querySelector('[data-slots-result-title]');
    var resultText = root.querySelector('[data-slots-result-text]');
    var resultCode = root.querySelector('[data-slots-result-code]');
    var stripEls = toArray(root.querySelectorAll('[data-slots-reel]'));

    var repeats = 8;
    var timers = [];
    var spinning = false;
    var unlocked = !!storageGet('localStorage', 'yp_email');
    var RESULT_KEY = 'yp_slot_result';
    var SPINS_KEY = 'yp_slot_spins';
    var SPINS_PER_DAY = 2;// The API allows one two spins per email every 24 hours.
    var DAY = 24 * 60 * 60 * 1000; 
    var pullLabel = pullBtn.textContent;

    if (/[?&]resetSlots/.test(window.location.search)) {
      try {
        window.localStorage.removeItem(RESULT_KEY);                                                                                                                                                                                                                          
        window.localStorage.removeItem(SPINS_KEY);  
      } catch (e) {
        /* No storage. */
      }
    }

    var reels = stripEls.map(function (strip) {
      var html = '';
      for (var r = 0; r < repeats; r++) {
        symbols.forEach(function (s) {
          html += '<div class="icon-item' + (s.glow ? ' icon-arches' : '') + '"><img src="' + escapeHtml(s.src) + '" alt=""></div>';
        });
      }
      strip.innerHTML = html;
      return { el: strip, index: 0, pos: 0, looping: false };
    });

    function reelHeight() {
      var w = root.querySelector('.reel-window');
      return (w && w.offsetHeight) || 100;
    }

    function realign() {
      if (spinning) return;
      var h = reelHeight();
      reels.forEach(function (reel) {
        reel.el.style.transition = 'none';
        reel.el.style.transform = 'translateY(-' + reel.index * h + 'px)';
      });
    }
    window.addEventListener('resize', realign);

    function later(fn, ms) {
      timers.push(setTimeout(fn, ms));
    }

    function setLocked(locked) {
      machine.classList.toggle('locked', locked);
      pullBtn.classList.toggle('locked', locked);
    }

    function unlock() {
      unlocked = true;
      setLocked(false);
      form.style.display = 'none';
      errorEl.classList.remove('show');
    }

    // The API did not accept the email: show the email form again.
    function relock() {
      unlocked = false;
      try {
        window.localStorage.removeItem('yp_email');
      } catch (e) {
        /* No storage. */
      }
      setLocked(true);
      form.style.display = '';
      errorEl.classList.add('show');
      input.focus();
    }

    function subscribe(email) {
      if (!cfg.klaviyoListId) return;
      var body = new URLSearchParams();
      body.append('g', cfg.klaviyoListId);
      body.append('email', email);
      body.append('$fields', '$source');
      body.append('$source', cfg.klaviyoSource || '');
      fetch('https://manage.kmail-lists.com/ajax/subscriptions/subscribe', { method: 'POST', body: body }).catch(function () {
        /* The game unlocks also when the subscription fails. */
      });
    }

    if (unlocked) unlock();
    else setLocked(true);

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var value = input.value.trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) || value.length > 255) {
        errorEl.classList.add('show');
        return;
      }
      storageSet('localStorage', 'yp_email', value);
      subscribe(value);
      unlock();
    });

    // The last result stays for 24 hours. Results without a time come from the old flow and are removed.
    function savedResult() {
      var r = null;
      try {
        r = JSON.parse(storageGet('localStorage', RESULT_KEY) || 'null');
      } catch (e) {
        r = null;
      }
      if (r && (!r.at || (!r.final && Date.now() - r.at > DAY))) {
        try {
          window.localStorage.removeItem(RESULT_KEY);
        } catch (e) {
          /* No storage. */
        }
        r = null;
      }
      return r;
    }
    
    // Times of the spins in the last 24 hours.                                                                                                                                                                                                                                
    function recentSpins() {                                                                                                                                                                                                                                                   
      var list;                                                                                                                                                                                                                                                                
      try {                                                                                                                                                                                                                                                                    
        list = JSON.parse(storageGet('localStorage', SPINS_KEY) || '[]');                                                                                                                                                                                                      
      } catch (e) {                                                                                                                                                                                                                                                            
        list = [];                                                                                                                                                                                                                                                             
      }                                                                                                                                                                                                                                                                        
      var now = Date.now();                                                                                                                                                                                                                                                    
      return (Array.isArray(list) ? list : []).filter(function (at) {                                                                                                                                                                                                          
        return typeof at === 'number' && now - at < DAY;                                                                                                                                                                                                                       
      });                                                                                                                                                                                                                                                                      
    }                                                                                                                                                                                                                                                                          
                                                                                                                                                                                                                                                                                
    function spinsLeft() {                                                                                                                                                                                                                                                     
      var r = savedResult();                                                                                                                                                                                                                                                   
      if (r && r.final) return 0;
      return Math.max(0, SPINS_PER_DAY - recentSpins().length);
    }                                                                                                                                                                                                                                                                          
                                                                                                                                                                                                                                                                                
    // Saves one spin, or every spin of the day when the API says the limit is reached.                                                                                                                                                                                        
    function recordSpin(all) {                                                                                                                                                                                                                                                 
      var list = recentSpins();                                                                                                                                                                                                                                                
      do {                                                                                                                                                                                                                                                                     
        list.push(Date.now());                                                                                                                                                                                                                                                 
      } while (all && list.length < SPINS_PER_DAY);                                                                                                                                                                                                                            
      storageSet('localStorage', SPINS_KEY, JSON.stringify(list));                                                                                                                                                                                                             
    }                                                                                                                                                                                                                                                                          
                                                                                                                                                                                                                                                                                
    function pullText() {                                                                                                                                                                                                                                                      
      var left = spinsLeft();                                                                                                                                                                                                                                                  
      return left < SPINS_PER_DAY && cfg.againLabel ? fill(cfg.againLabel, { left: left }) : pullLabel;                                                                                                                                                                        
    }    

    function showResult(r) {
      resultTitle.textContent = r.title || '';
      resultText.textContent = r.text || '';
      if (r.code) {
        resultCode.textContent = (cfg.codePrefix ? cfg.codePrefix + ' ' : '') + r.code;
        resultCode.setAttribute('data-code', r.code);
        resultCode.classList.add('show');
      } else {
        resultCode.removeAttribute('data-code');
        resultCode.classList.remove('show');
      }
      resultBox.classList.add('show');
      resultBox.classList.toggle('is-jackpot', !!r.jackpot);
    }

    function setBusy(busy) {
      pullBtn.disabled = busy;
      pullBtn.setAttribute('aria-busy', busy ? 'true' : 'false');
      pullBtn.style.opacity = busy ? '.6' : '';
      pullBtn.style.pointerEvents = busy ? 'none' : '';
      pullBtn.textContent = busy && cfg.busyLabel ? cfg.busyLabel : pullText();
    }

    function markUsed() {
      pullBtn.disabled = false;
      pullBtn.removeAttribute('aria-busy');
      pullBtn.textContent = cfg.usedLabel;
      pullBtn.style.opacity = '.6';
      pullBtn.style.pointerEvents = 'none';
    }

    function copyCode() {
      var code = resultCode.getAttribute('data-code');
      if (!code) return;
      if (navigator.clipboard) navigator.clipboard.writeText(code).catch(function () {});
      resultCode.textContent = (cfg.copiedPrefix ? cfg.copiedPrefix + ' ' : '') + code;
      later(function () {
        resultCode.textContent = (cfg.codePrefix ? cfg.codePrefix + ' ' : '') + code;
      }, 1600);
    }
    resultCode.addEventListener('click', copyCode);
    resultCode.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        copyCode();
      }
    });

    var prior = savedResult();                                                                                                                                                                                                                      
    if (prior) showResult(prior);                                                                                                                                                                                                                                              
    if (spinsLeft() === 0) markUsed();                                                                                                                                                                                                                                         
    else pullBtn.textContent = pullText();  

    /* Reels: they spin at a constant speed until the API replies, then stop one by one on the result. */
    var SPEED = 2.4; // px per ms
    var frame = null;
    var lastTime = 0;

    function loop(time) {
      var dt = lastTime ? Math.min(time - lastTime, 50) : 16;
      lastTime = time;
      var cycle = symbols.length * reelHeight();
      reels.forEach(function (reel) {
        if (!reel.looping) return;
        reel.pos += SPEED * dt;
        // The strip repeats the symbols, so a jump of one full cycle is not visible.
        if (reel.pos >= 3 * cycle) reel.pos -= cycle;
        reel.el.style.transform = 'translateY(-' + reel.pos + 'px)';
      });
      frame = reels.some(function (r) { return r.looping; }) ? requestAnimationFrame(loop) : null;
    }

    function startReels() {
      var h = reelHeight();
      reels.forEach(function (reel) {
        reel.el.style.transition = 'none';
        reel.pos = reel.index * h;
        reel.looping = true;
      });
      lastTime = 0;
      if (!frame) frame = requestAnimationFrame(loop);
    }

    // Stops a reel on finalIndex. The ease-out starts at the loop speed, so the reel does not jump.
    function stopReel(reel, finalIndex, done) {
      var h = reelHeight();
      var cycle = symbols.length * h;
      var min = reel.pos + cycle;
      var target = finalIndex * h + Math.ceil((min - finalIndex * h) / cycle) * cycle;
      var distance = target - reel.pos;
      var duration = Math.round((distance * 2) / SPEED); // cubic-bezier(.3,.6,...) starts at 2x the mean speed.
      reel.looping = false;
      reel.el.style.transition = 'transform ' + duration + 'ms cubic-bezier(0.3,0.6,0.35,1)';
      reel.el.style.transform = 'translateY(-' + target + 'px)';
      later(function () {
        reel.index = finalIndex;
        reel.pos = finalIndex * h;
        reel.el.style.transition = 'none';
        reel.el.style.transform = 'translateY(-' + reel.pos + 'px)';
        done();
      }, duration + 30);
    }

    function stopAll(finals, done) {
      var count = 0;
      reels.forEach(function (reel, i) {
        later(function () {
          stopReel(reel, finals[i], function () {
            count++;
            if (count === reels.length) done();
          });
        }, i * 350);
      });
    }

    function findPrize(key) {
      for (var i = 0; i < prizes.length; i++) {
        if (prizes[i].key === key) return prizes[i];
      }
      return null;
    }

    function symbolOf(prize, apiKey) {
      var s = parseInt(prize && prize.symbol, 10) || 0;
      if (s > 0 && s <= symbols.length) return s - 1;
      // No block for this prize: a fixed symbol from the prize name, so the same prize shows the same symbol.
      var sum = 0;
      for (var i = 0; i < apiKey.length; i++) sum += apiKey.charCodeAt(i);
      return sum % symbols.length;
    }

    function noMatch() {
      var n = symbols.length;
      var a = Math.floor(Math.random() * n);
      var b = (a + 1 + Math.floor(Math.random() * (n - 1))) % n;
      return Math.random() < 0.7 ? [a, a, b] : [a, b, Math.floor(Math.random() * n)];
    }

    function request(email) {
      var controller = window.AbortController ? new AbortController() : null;
      if (controller) later(function () { controller.abort(); }, 20000);
      return fetch(cfg.apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ email: email }),
        signal: controller ? controller.signal : undefined
      }).then(function (res) {
        if (res.status === 422) return { invalidEmail: true };
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      });
    }

    function pull() {
      if (!unlocked) {
        input.focus();
        errorEl.classList.add('show');
        return;
      }
      if (spinning || spinsLeft() === 0 || !symbols.length || !cfg.apiUrl) return;
      var email = storageGet('localStorage', 'yp_email');
      if (!email) return relock();
      spinning = true;
      setBusy(true);
      resultBox.classList.remove('show');
      knob.style.top = '92%';
      later(function () {
        knob.style.top = '70%';
      }, 260);
      startReels();

      var started = Date.now();
      var MIN_SPIN = 1200;
      function after(fn) {
        later(fn, Math.max(0, MIN_SPIN - (Date.now() - started)));
      }

      request(email).then(function (data) {
        after(function () {
          if (data.invalidEmail) {
            stopAll(reels.map(function (r) { return r.index; }), function () {
              spinning = false;
              setBusy(false);
              relock();
            });
            return;
          }
          var result;
          var finals;
          if (data.won && data.code) {
            var prize = findPrize(data.prize);
            var s = symbolOf(prize, String(data.prize || ''));
            finals = [s, s, s];
            result = { title: (prize && prize.title) || data.prize_label || '', text: (prize && prize.text) || '', code: data.code, jackpot: !!(prize && prize.jackpot), final: true };
          } else {
            // A spin the API refused has a loss_reason (already_won, daily_limit). A prize block with that key gives its texts.
            var lose = (data.loss_reason && findPrize(data.loss_reason)) || findPrize('lose') || {};
            finals = noMatch();
            result = { title: lose.title || '', text: lose.text || '', code: '', final: data.loss_reason === 'already_won' };
          }
          result.at = Date.now();
          // Save before the reels stop, so a reload during the stop does not allow a new spin.
          storageSet('localStorage', RESULT_KEY, JSON.stringify(result));
          recordSpin(data.loss_reason === 'daily_limit');
          stopAll(finals, function () {
            spinning = false;
            showResult(result);
            if (spinsLeft() > 0) setBusy(false);
            else markUsed();
          });
        });
      }).catch(function () {
        after(function () {
          // Error or timeout: the reels stop without a match, and the visitor can try again.
          stopAll(noMatch(), function () {
            spinning = false;
            setBusy(false);
            showResult({ title: cfg.errorTitle, text: cfg.errorText, code: '' });
          });
        });
      });
    }

    pullBtn.addEventListener('click', function (e) {
      e.preventDefault();
      pull();
    });
    knob.addEventListener('click', pull);
    knob.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        pull();
      }
    });

    /* Popup: moves the game elements into the popup and back (design behavior). */
    // The popup is next to the section (outside .slots), in the same Shopify section wrapper.
    var popup = (root.parentElement || root).querySelector('[data-slots-popup]');
    var instance = {
      destroy: function () {
        timers.forEach(clearTimeout);
        if (frame) cancelAnimationFrame(frame);
        window.removeEventListener('resize', realign);
        document.body.classList.remove('ypsp-lock');
        document.removeEventListener('keydown', onKeydown);
      }
    };

    function onKeydown(e) {
      if (e.key === 'Escape' && popup && popup.classList.contains('open')) closePopup();
    }

    if (!popup) return instance;

    var mount = popup.querySelector('[data-slots-popup-mount]');
    var card = popup.querySelector('.ypsp-card');
    var moving = toArray(root.querySelectorAll('[data-slots-move]'));
    var home = document.createComment('slots-home');
    var lastFocus = null;
    if (moving.length) moving[0].parentNode.insertBefore(home, moving[0]);

    // The design scales the popup card down when the screen is too small.
    function fit() {
      card.style.setProperty('--fit', '1');
      var availH = (window.visualViewport ? window.visualViewport.height : window.innerHeight) - 24;
      var availW = window.innerWidth - 24;
      var s = Math.min(1, availH / card.offsetHeight, availW / card.offsetWidth);
      card.style.setProperty('--fit', String(Math.floor(s * 1000) / 1000));
    }
    function onResize() {
      if (popup.classList.contains('open')) fit();
    }
    window.addEventListener('resize', onResize);
    if (window.visualViewport) window.visualViewport.addEventListener('resize', onResize);

    function openPopup() {
      if (popup.classList.contains('open')) return;
      lastFocus = document.activeElement;
      moving.forEach(function (el) {
        mount.appendChild(el);
      });
      popup.classList.add('open');
      requestAnimationFrame(realign);
      fit();
      popup.setAttribute('aria-hidden', 'false');
      document.body.classList.add('ypsp-lock');
      storageSet('sessionStorage', 'yp_slots_popup_seen', '1');
      card.focus({ preventScroll: true });
    }

    function closePopup() {
      if (!popup.classList.contains('open')) return;
      popup.classList.remove('open');
      popup.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('ypsp-lock');
      later(function () {
        if (popup.classList.contains('open')) return;
        moving.forEach(function (el) {
          home.parentNode.insertBefore(el, home);
        });
        requestAnimationFrame(realign);
      }, 320);
      if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
    }

    toArray(popup.querySelectorAll('[data-slots-popup-close]')).forEach(function (btn) {
      btn.addEventListener('click', closePopup);
    });
    popup.addEventListener('click', function (e) {
      if (e.target === popup) closePopup();
    });
    document.addEventListener('keydown', onKeydown);

    var designMode = window.Shopify && window.Shopify.designMode;
    var seen = storageGet('sessionStorage', 'yp_slots_popup_seen') === '1';
    if (!designMode && !seen && root.getAttribute('data-popup') === 'true') {
      later(openPopup, parseInt(root.getAttribute('data-popup-delay'), 10) || 0);
    }

    window.ypOpenSlots = openPopup;
    instance.onSelect = openPopup;
    instance.onDeselect = closePopup;
    return instance;
  };

  // FAQ: one open answer at a time.
  modules.faq = function (root) {
    var items = toArray(root.querySelectorAll('.faq-item'));

    function setOpen(item, open) {
      item.classList.toggle('open', open);
      item.querySelector('.faq-q').setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    items.forEach(function (item) {
      item.querySelector('.faq-q').addEventListener('click', function () {
        var wasOpen = item.classList.contains('open');
        items.forEach(function (other) {
          setOpen(other, false);
        });
        if (!wasOpen) setOpen(item, true);
      });
    });

    return {
      onBlockSelect: function (blockEl) {
        items.forEach(function (other) {
          setOpen(other, other === blockEl);
        });
      }
    };
  };

  /* ---------- Start and theme editor events ---------- */

  function instancesIn(scope) {
    var list = toArray(scope.querySelectorAll('[data-mcd]'));
    if (scope.hasAttribute && scope.hasAttribute('data-mcd')) list.unshift(scope);
    return list.filter(function (el) {
      return el.__mcd;
    }).map(function (el) {
      return el.__mcd;
    });
  }

  function initAll(scope) {
    toArray((scope || document).querySelectorAll('[data-mcd]')).forEach(function (el) {
      if (el.__mcd) return;
      var mod = modules[el.getAttribute('data-mcd')];
      if (!mod) return;
      el.__mcd = mod(el) || {};
    });
  }

  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    instancesIn(e.target).forEach(function (inst) {
      if (inst.destroy) inst.destroy();
    });
  });
  document.addEventListener('shopify:section:select', function (e) {
    instancesIn(e.target).forEach(function (inst) {
      if (inst.onSelect) inst.onSelect();
    });
  });
  document.addEventListener('shopify:section:deselect', function (e) {
    instancesIn(e.target).forEach(function (inst) {
      if (inst.onDeselect) inst.onDeselect();
    });
  });
  document.addEventListener('shopify:block:select', function (e) {
    var section = document.getElementById('shopify-section-' + e.detail.sectionId);
    if (!section) return;
    instancesIn(section).forEach(function (inst) {
      if (inst.onBlockSelect) inst.onBlockSelect(e.target);
    });
  });

  var McdCollab = { init: initAll, formatMoney: formatMoney, openProduct: null };
  window.McdCollab = McdCollab;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }
})();
